import React, { useState } from 'react';
import { MaintenanceRecord } from '../../types/maintenance.ts';
import { formatCurrency, formatDate } from '../../utils/formatters.ts';
import MaintenanceForm from './MaintenanceForm.tsx';
import { useMaintenanceRecords } from '../../hooks/useMaintenanceRecords.ts';
import '../../styles/Maintenance.css';

interface MaintenanceRecordsProps {
	propertyId: string;
	onMaintenanceChange?: () => Promise<void>;
}

const getMaintenanceTypeEmoji = (type: string) => {
	switch (type) {
		case 'Repair':
			return '🔧';
		case 'Renovation':
			return '🏗️';
		case 'Regular':
			return '🔄';
		case 'Emergency':
			return '🚨';
		default:
			return '📝';
	}
};

const getStatusClass = (status: string) => {
	switch (status.toLowerCase().replace(/\s+/g, '-')) {
		case 'completed':
			return 'status-completed';
		case 'pending':
			return 'status-pending';
		case 'in-progress':
			return 'in-progress';
		case 'cancelled':
			return 'status-cancelled';
		default:
			return '';
	}
};

const MaintenanceRecords: React.FC<MaintenanceRecordsProps> = ({
	propertyId,
	onMaintenanceChange,
}) => {
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [selectedRecord, setSelectedRecord] =
		useState<MaintenanceRecord | null>(null);
	const { records, loading, error, addRecord, updateRecord, deleteRecord } =
		useMaintenanceRecords(propertyId);

	if (loading) return <div>Loading maintenance records...</div>;
	if (error) return <div>Error loading maintenance records</div>;

	const handleMaintenanceUpdate = async (data: any) => {
		if (selectedRecord) {
			await updateRecord(selectedRecord.id, data);
		} else {
			await addRecord(data);
		}
		onMaintenanceChange?.();
	};

	return (
		<div className='maintenance-section'>
			<div className='section-header'>
				<h3>Maintenance Records</h3>
				<button
					className='btn-primary'
					onClick={() => setIsFormOpen(true)}
				>
					Record a Maintenance
				</button>
			</div>

			<div className='maintenance-list'>
				{records.map((record) => (
					<div key={record.id} className='maintenance-card'>
						<div className='maintenance-header'>
							<span
								className={`status-badge ${getStatusClass(
									record.status
								)}`}
							>
								<span className='status-dot'></span>
								{record.status}
							</span>
							<span className='type'>
								{getMaintenanceTypeEmoji(
									record.maintenance_type
								)}{' '}
								{record.maintenance_type}
							</span>
						</div>

						<div className='maintenance-details'>
							<p>{record.description}</p>
							<p className='cost'>
								Cost: {formatCurrency(record.cost)}
							</p>
							<p className='dates'>
								{formatDate(record.start_date)}
								{record.end_date &&
									` - ${formatDate(record.end_date)}`}
							</p>
							{record.vendor_details && (
								<p className='vendor'>
									Vendor: {record.vendor_details}
								</p>
							)}
						</div>

						<div className='maintenance-actions'>
							<button
								onClick={() => setSelectedRecord(record)}
								className='btn-edit'
							>
								Edit
							</button>
							<button
								onClick={() => {
									if (
										window.confirm(
											'Delete this maintenance record?'
										)
									) {
										deleteRecord(record.id);
									}
								}}
								className='btn-delete'
							>
								Delete
							</button>
						</div>
					</div>
				))}

				{records.length === 0 && (
					<p className='no-records'>No maintenance records found</p>
				)}
			</div>

			{(isFormOpen || selectedRecord) && (
				<MaintenanceForm
					propertyId={propertyId}
					record={selectedRecord}
					onSubmit={handleMaintenanceUpdate}
					onClose={() => {
						setIsFormOpen(false);
						setSelectedRecord(null);
					}}
				/>
			)}
		</div>
	);
};

export default MaintenanceRecords;
